import React, { useEffect, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useToggle } from 'react-use';
import { InputBox } from '@infoods/lifebase-ui';

import { UserProfileFromValue } from '../UserProfileStep.settings';
import {
  AddScanManualForm,
  AddScanManualFormValues,
  EntityInbodyData,
  EntryFieldConfig,
  FailedUploadScan,
  inbodyEntryFieldConfig,
  ScanInfoTable,
  UseCustomerScanReturn,
} from '../../../../../../inbody';
import { INBODY_TYPES } from '../../../../../../shared/constants/inbody-info/InbodyType';
import { ScanButtonsRow } from './ScanButtonsRow/ScanButtonsRow';

type AddInbodyScanProps = {
  entityInbodyData: EntityInbodyData | null;
  defaultManualScanData: AddScanManualFormValues['scanData'] | undefined;
} & UseCustomerScanReturn;

const AddInbodyScan = ({
  entityInbodyData,
  defaultManualScanData,
  scanData,
  isScanUnavailable,
  getScanData,
}: AddInbodyScanProps) => {
  const [isShowAddScanForm, toggleShowAddScanForm] = useToggle(false);
  const [addScanConfig, setAddScanConfig] = useState<EntryFieldConfig | null>(null);

  const { trigger, getValues, setValue, watch, resetField } =
    useFormContext<UserProfileFromValue>();

  const inbodyType = watch('inbodyInfo.inbodyType');

  const handleLoadScan = async () => {
    const isValid = await trigger('inbodyInfo.inbodyId', { shouldFocus: true });
    const inbodyId = getValues('inbodyInfo.inbodyId');

    if (inbodyId && isValid) {
      const matchedCustomer = await getScanData(inbodyId);

      // the lookup also matches by user token, so the typed value may be the phone and not the InBody ID
      if (matchedCustomer?.inbodyId) {
        setValue('inbodyInfo.inbodyId', matchedCustomer.inbodyId, {
          shouldValidate: true,
        });
      }
      toggleShowAddScanForm(false);
    }
  };

  const handleFillInDataManually = (isShow?: boolean) => {
    toggleShowAddScanForm(isShow);
  };

  useEffect(() => {
    if (!inbodyType) {
      handleFillInDataManually(false);
      setAddScanConfig(null);
    } else if (inbodyType === INBODY_TYPES.NON_INBODY) {
      handleFillInDataManually(true);
      setAddScanConfig(entityInbodyData?.nonInbodyData!);
    } else {
      handleFillInDataManually(false);
      setAddScanConfig(inbodyEntryFieldConfig[inbodyType]);
    }
  }, [inbodyType]);

  // need add default scan data each time when user open add scan form
  // because shouldUnregister: true - delete also default values what not used by inputs
  useEffect(() => {
    if (isShowAddScanForm) {
      resetField('scanData', { defaultValue: defaultManualScanData });
    }
  }, [isShowAddScanForm]);

  const isScanExist = !!scanData;
  const isShowFailedUploadScan = isScanUnavailable && !isShowAddScanForm;
  const isInbodyType = inbodyType && inbodyType !== INBODY_TYPES.NON_INBODY;

  return (
    <>
      {isInbodyType && (
        <ScanButtonsRow
          onLoadScan={handleLoadScan}
          onFillInDataManually={handleFillInDataManually}
          hideFillInDataManually={isScanExist}
        />
      )}
      {isShowFailedUploadScan && <FailedUploadScan />}
      {isScanExist && !isShowAddScanForm && <ScanInfoTable scanData={scanData} />}
      {isShowAddScanForm && addScanConfig && (
        <InputBox title="Manual Data" variant="innerBox">
          <AddScanManualForm
            entryFieldConfig={addScanConfig}
            scanDateTitle="Check-in-date"
            scanTimeTitle="Check-in-time"
          />
        </InputBox>
      )}
    </>
  );
};

export { AddInbodyScan };
